import React, { useEffect, useState } from 'react';
import auth from '../helper/auth'; 
import config from '../config';
import PricingPlanMultipleColor from './child/PricingPlanMultipleColor'; 
import { toast } from 'react-toastify';

const MySubscriptionLayer = () => {
    const [subscription, setSubscription] = useState(null);
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('overview'); // 'overview' or 'plans'

    const fetchSubscription = async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/subscription`);
            const data = await response.json();
            setSubscription(data.subscription);
            setUsage(data.usage);
        } catch (error) {
            console.error('Error fetching subscription:', error);
            toast.error('Failed to load subscription details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubscription();
    }, []);

    const handleCancel = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription?')) return;

        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/cancel-subscription`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ subscriptionId: subscription.id })
            });
            const data = await response.json();
            if (response.ok) {
                toast.success('Subscription canceled successfully');
                fetchSubscription();
            } else {
                toast.error(data.error || 'Failed to cancel subscription');
            }
        } catch (error) {
            console.error('Error canceling:', error);
            toast.error('An error occurred');
        }
    };

    const handleUpdatePlan = async (plan) => {
        if (window.confirm(`Switch to ${plan.name}? This will update your subscription immediately.`)) {
            try {
                const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/update-subscription`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        subscriptionId: subscription.id,
                        newPriceId: plan.stripe_price_id 
                    })
                });
                const data = await response.json();
                if (response.ok) {
                     // Check for redirect URL (SCA / Payment required)
                     if (data.approvalUrl) {
                         console.log('[DEBUG] Payment required. Redirecting to:', data.approvalUrl);
                         toast.info('Redirecting to payment gateway...');
                         window.location.href = data.approvalUrl;
                         return;
                     }

                     // Verify status
                     const subStatus = data.subscription?.status || data.status; // Fallback if subscription object is missing
                     if (subStatus === 'active' || subStatus === 'trialing') {
                        toast.success('Plan updated successfully');
                        setView('overview');
                        fetchSubscription();
                     } else {
                        toast.warning(`Subscription updated but status is ${subStatus}. Check payment details.`);
                        fetchSubscription(); // Still fetch to show latest state
                     }
                } else {
                    toast.error(data.error || 'Failed to update plan');
                }
            } catch (error) {
                console.error('Error updating plan:', error);
                toast.error('An error occurred');
            }
        }
    };

    if (loading) return <div className="text-center p-5">Loading subscription details...</div>;

    // If no active subscription or user wants to change plan
    if (!subscription || view === 'plans') {
        return (
            <div>
                {subscription && (
                    <button onClick={() => setView('overview')} className="btn btn-secondary mb-3">
                        &larr; Back to My Subscription
                    </button>
                )}
                <PricingPlanMultipleColor onSubscribe={subscription ? handleUpdatePlan : undefined} />
            </div>
        );
    }

    // Subscription Overview
    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                <h6 className="text-lg fw-semibold mb-0">My Subscription</h6>
                <span className={`badge ${subscription.status === 'active' ? 'bg-success-focus text-success-main' : 'bg-danger-focus text-danger-main'} px-16 py-8 radius-8`}>
                    {subscription.status.toUpperCase()}
                </span>
            </div>
            <div className="card-body p-24">
                <div className="row gy-4">
                    <div className="col-lg-6">
                        <div className="p-16 border radius-12 bg-base">
                            <span className="text-secondary-light text-sm mb-4 d-block">Current Plan</span>
                            <h4 className="mb-0">{subscription.plan_name}</h4>
                            <p className="text-primary-600 fw-medium mt-8">
                                {subscription.currency.toUpperCase()} {subscription.price} / {subscription.interval || 'month'}
                            </p>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="p-16 border radius-12 bg-base">
                            <span className="text-secondary-light text-sm mb-4 d-block">Renewal Date</span>
                            <h4 className="mb-0">
                                {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'N/A'}
                            </h4>
                            <p className="text-secondary-light text-sm mt-8">
                                Auto-renewal is {subscription.cancel_at_period_end ? 'OFF' : 'ON'}
                            </p>
                        </div>
                    </div>
                    
                    <div className="col-12 mt-24">
                        <h6 className="text-md fw-semibold mb-16">Usage & Limits</h6>
                        <div className="row gy-4">
                            <div className="col-md-6">
                                <div className="d-flex justify-content-between mb-8">
                                    <span className="text-secondary-light">Venues</span>
                                    <span className="fw-medium">{usage.venues} / {usage.max_venues}</span>
                                </div>
                                <div className="progress h-8-px radius-4 bg-primary-50">
                                    <div 
                                        className="progress-bar bg-primary-600 radius-4" 
                                        role="progressbar" 
                                        style={{ width: `${Math.min((usage.venues / usage.max_venues) * 100, 100)}%` }}
                                    ></div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex justify-content-between mb-8">
                                    <span className="text-secondary-light">Features per Venue</span>
                                    <span className="fw-medium">Max {usage.max_features}</span>
                                </div>
                                <div className="progress h-8-px radius-4 bg-primary-50">
                                    <div 
                                        className="progress-bar bg-info-600 radius-4" 
                                        role="progressbar" 
                                        style={{ width: '100%' }} // Fixed visual for features
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 mt-32 d-flex gap-3">
                        <button 
                            onClick={() => setView('plans')} 
                            className="btn btn-primary radius-8 px-24"
                        >
                            Upgrade / Change Plan
                        </button>
                        {subscription.status === 'active' && !subscription.cancel_at_period_end && (
                            <button 
                                onClick={handleCancel} 
                                className="btn btn-danger-soft radius-8 px-24 text-danger-main"
                            >
                                Cancel Subscription
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MySubscriptionLayer;
